/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useMemo, useRef, useState, useContext} from 'react'
import {useLocation} from 'react-router-dom'
import debounce from 'lodash/debounce'

import {
    Input,
    InputGroup,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Box,
    Flex,
    HStack,
    InputRightElement,
    VisuallyHidden
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useSearchSuggestions, useProductSearch} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {usePopularSuggestions} from '../../hooks/use-popular-suggestion'
import {useTrackThinkAnalyticsEvents} from '../../hooks/use-track-thinkanalytics-events.js'
import {useSiteCustomPreferences} from '../../hooks/use-site-custom-preferences'
import {SegmentContext} from '../../integrations/segment/app/contexts/index.js'

import SearchSuggestions from './partials/search-suggestions'
import SearchImg from '../header/SearchImg'
import {
    searchActionSubmitted,
    searchSuggetionsSubmitted
} from '../../integrations/segment/app/page/home.js'
import {productUrlBuilder, searchUrlBuilder, categoryUrlBuilder} from '../../utils/url'
import {
    capitalize,
    boldString,
    getSessionJSONItem,
    setSessionJSONItem
} from '@salesforce/retail-react-app/app/utils/utils'
import {
    RECENT_SEARCH_KEY,
    RECENT_SEARCH_LIMIT,
    RECENT_SEARCH_MIN_LENGTH
} from '@salesforce/retail-react-app/app/constants'

const formatSuggestions = (currentSearchSuggestions, input, products, isMobileAppRestricted) => {
    const searchSuggestions = isMobileAppRestricted
        ? {
              ...currentSearchSuggestions,
              hits: currentSearchSuggestions?.hits?.filter((product) => {
                  return !products?.[product?.productId]
              })
          }
        : currentSearchSuggestions
    const totalCount = searchSuggestions?.totalResults

    return {
        categorySuggestions: searchSuggestions?.categorySuggestions?.categories?.map(
            (suggestion) => ({
                type: 'category',
                id: suggestion.id,
                link: categoryUrlBuilder({id: suggestion.id}),
                name: boldString(suggestion.name, capitalize(input)),
                parentCategoryName: suggestion.parentCategoryName
            })
        ),
        productSuggestions: searchSuggestions?.hits?.map((product) => ({
            type: 'product',
            currency: product.currency,
            price: product.price,
            productId: product.productId,
            name: boldString(product.productName, capitalize(input)),
            link: productUrlBuilder({id: product.productId}),
            image: product.image,
            productData: product
        })),
        popularSuggestions: searchSuggestions?.popularPhrases?.map((phrase) => ({
            link: phrase.url,
            value: phrase.value
        })),
        categorySearchPhrase: searchSuggestions?.categorySuggestions?.suggestedPhrases?.map(
            (suggestedPhrases) => ({
                type: 'category',
                name: suggestedPhrases.phrase
            })
        ),
        totalCount,
        input
    }
}

const Search = (props) => {
    const {getcustomPreferencevalue} = useSiteCustomPreferences()
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const navigate = useNavigation()
    const isEnabled = searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH
    const isTAEnabled = getcustomPreferencevalue('pwaThinkAnalyticsEnabled')
    const {mutate: trackTAEvent} = useTrackThinkAnalyticsEvents()
    const searchInputRef = useRef()
    const wrapperRef = useRef()
    const suggestionListRef = useRef()
    const location = useLocation()
    const segmentContext = useContext(SegmentContext)
    const configs = getConfig()

    // Clear input on route change
    useEffect(() => {
        clearInput()
    }, [location?.pathname])

    const searchSuggestion = useSearchSuggestions(
        {parameters: {q: searchQuery, limit: 3}},
        {enabled: isEnabled}
    )
    const popularSuggestions = usePopularSuggestions(
        {parameters: {q: searchQuery, limit: 3}},
        {enabled: isEnabled}
    )
    const productSearch = useProductSearch(
        {
            parameters: {
                q: searchQuery,
                perPricebook: true,
                allVariationProperties: true,
                allImages: true,
                expand: [
                    'availability',
                    'images',
                    'prices',
                    'variations',
                    'represented_products',
                    'custom_properties'
                ],
                limit: 3
            }
        },
        {enabled: isEnabled}
    )

    const recentSearches = getSessionJSONItem(RECENT_SEARCH_KEY)
    const hits = productSearch?.data?.hits
    const categorySuggestions = searchSuggestion?.data?.categorySuggestions
    const popularPhrases = popularSuggestions?.phrases || popularSuggestions?.data?.phrases
    const totalResults = productSearch?.data?.total
    const suggestions = {hits, categorySuggestions, popularPhrases, totalResults}
    const isMobileAppRestrictedSrc = configs?.app?.login?.social?.mobileAppRestricted || 'mobileapp'
    const isMobileAppRestricted =
        typeof window !== 'undefined' &&
        localStorage.getItem('sourceCode') === isMobileAppRestrictedSrc
    const productSuggestions =
        isMobileAppRestricted &&
        searchSuggestion?.data?.productSuggestions?.products?.reduce((obj, product) => {
            obj[product?.productId] = product?.c_isSubscriptionProduct
            return obj
        }, {})
    const searchSuggestions = useMemo(
        () =>
            formatSuggestions(
                suggestions,
                searchInputRef?.current?.value,
                productSuggestions,
                isMobileAppRestricted
            ),
        [searchSuggestion?.data, productSearch?.data, popularPhrases]
    )

    const [noResult, setNoResult] = useState({})
    useEffect(() => {
        if (searchQuery?.length >= RECENT_SEARCH_MIN_LENGTH && searchSuggestions) {
            setNoResult({searchQuery: searchQuery, showNoResultMessage: true})
        }
    }, [categorySuggestions?.categories, hits, popularPhrases, searchQuery])

    useEffect(() => {
        shouldOpenPopover()
    }, [searchQuery, searchSuggestion?.data, productSearch?.data, popularPhrases])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const searchSuggestionsAvailable =
        searchSuggestions &&
        (searchSuggestions?.categorySuggestions?.length ||
            searchSuggestions?.productSuggestions?.length ||
            searchSuggestions?.popularSuggestions?.length)

    const saveRecentSearch = (searchText) => {
        let searches = getSessionJSONItem(RECENT_SEARCH_KEY) || []
        searches = searches?.filter((s) => s.toLowerCase() !== searchText.toLowerCase())
        searches = [searchText, ...searches].slice(0, RECENT_SEARCH_LIMIT)
        setSessionJSONItem(RECENT_SEARCH_KEY, searches)
    }

    const debouncedSearch = debounce((input) => {
        debouncedSearch.cancel()
        setSearchQuery(input)
    }, 300)

    const waitForSuggestionsToBeReady = async (query, maxWait = 1000) => {
        const pollInterval = 50
        let waited = 0

        return new Promise((resolve) => {
            const interval = setInterval(() => {
                const ready =
                    searchSuggestion?.data &&
                    productSearch?.data &&
                    searchInputRef?.current?.value.trim() === query

                if (ready || waited >= maxWait) {
                    clearInterval(interval)
                    resolve()
                }

                waited += pollInterval
            }, pollInterval)
        })
    }

    const onSubmitSearch = async (e) => {
        e.preventDefault()
        let searchText = searchInputRef?.current?.value?.trim()
        if (searchText?.length < 1) searchText = 'emptysearch'

        setSearchQuery(searchText)
        setIsOpen(true)
        await waitForSuggestionsToBeReady(searchText)

        saveRecentSearch(searchText)
        // segment event
        searchActionSubmitted(searchText, segmentContext)

        clearInput()
        navigate(searchUrlBuilder(searchText))
    }

    const closeAndNavigate = (link, messageText) => {
        clearInput()
        setIsOpen(false)

        if (link) {
            const selectedProductId = link?.split('/').pop()
            const searchText = searchInputRef?.current?.value.trim()

            if (isTAEnabled) {
                trackTAEvent({
                    c_productId: selectedProductId,
                    c_refUseCase: 'CRSTORE',
                    c_useCase: 'Store%2FLearn%2FClickedOnSearch',
                    additionalData: {isSearch: true}
                })
            }
            // segement event
            const textOfButtonOrLink = messageText?.replace(/<[^>]*>/g, '') || ' '
            segmentContext.linkText = textOfButtonOrLink
            searchSuggetionsSubmitted(
                searchText,
                searchSuggestions,
                selectedProductId,
                segmentContext
            )

            navigate(link)
        }
    }

    const shouldOpenPopover = () => {
        const inputValue = searchInputRef?.current?.value || ''
        if (inputValue?.length === 0) return setIsOpen(false)

        if (
            (document?.activeElement?.id === 'search-input' && recentSearches?.length > 0) ||
            (searchSuggestionsAvailable && inputValue.length > 0) ||
            (!searchSuggestionsAvailable && inputValue.length >= RECENT_SEARCH_MIN_LENGTH)
        ) {
            setIsOpen(true)
        } else {
            setIsOpen(false)
        }
    }

    const onSearchChange = (e) => {
        const input = e.target.value
        if (input.length >= RECENT_SEARCH_MIN_LENGTH) {
            debouncedSearch(input)
        } else {
            setSearchQuery('')
        }
    }

    const onSearchInputChange = (e) => {
        onSearchChange(e)
        shouldOpenPopover()
    }

    const clearInput = () => {
        if (searchInputRef?.current) {
            searchInputRef.current.value = ''
        }
        setIsOpen(false)
    }

    const customScrollbar = {
        '&::-webkit-scrollbar': {width: '6px'},
        '&::-webkit-scrollbar-thumb': {borderRadius: '10px', backgroundColor: '#F8F8F8'},
        '&::-webkit-scrollbar-track': {borderRadius: 'none', backgroundColor: '#23252b'}
    }

    return (
        <Popover isOpen={isOpen} isLazy initialFocusRef={searchInputRef}>
            <Box ref={wrapperRef} as="nav" aria-labelledby="search-label" className="search-input">
                <PopoverTrigger>
                    <form onSubmit={onSubmitSearch} id="search-form">
                        <HStack>
                            <InputGroup>
                                <VisuallyHidden id="search-label" htmlFor="search-input">
                                    Search the site
                                </VisuallyHidden>
                                <Input
                                    autoComplete="off"
                                    id="search-input"
                                    onChange={onSearchInputChange}
                                    onFocus={shouldOpenPopover}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            setSearchQuery(e.target.value.trim())
                                            setIsOpen(true)
                                        }
                                        if (e.key === 'Tab' && e.shiftKey) {
                                            const tabbables =
                                                suggestionListRef.current?.querySelectorAll(
                                                    '[tabindex="0"]'
                                                )
                                            if (tabbables?.length > 0) {
                                                e.preventDefault()
                                                tabbables[tabbables.length - 1].focus()
                                            }
                                        }
                                    }}
                                    type="text"
                                    ref={searchInputRef}
                                    {...props}
                                    className="site-search-input-box"
                                    border={'none'}
                                    _focusVisible={{
                                        bg: 'inputFocusBgColor',
                                        borderColor: 'rgb(96.5, 210.421875, 255)',
                                        outline: 0,
                                        boxShadow: '0 0 0 .2rem rgba(0, 161, 224, .25)'
                                    }}
                                    aria-haspopup="listbox"
                                    aria-expanded={isOpen}
                                    aria-controls="search-results-popover"
                                    aria-autocomplete="list"
                                    role="combobox"
                                />
                                <InputRightElement
                                    as={'button'}
                                    cursor="pointer"
                                    className="site-search-icon-box"
                                    onClick={onSubmitSearch}
                                    aria-label="Submit search keywords"
                                >
                                    <SearchImg />
                                    <VisuallyHidden>Search</VisuallyHidden>
                                </InputRightElement>
                            </InputGroup>
                        </HStack>

                        <HideOnMobile className="hideMobile" position="relative">
                            {(searchSuggestion.isSuccess || productSearch.isSuccess) && (
                                <PopoverContent
                                    ref={suggestionListRef}
                                    transform="none !important"
                                    data-testid="sf-suggestion-popover"
                                    id="search-results-popover"
                                    role="listbox"
                                    w="100%"
                                    position="absolute"
                                    overflowY="auto"
                                    maxH="500px"
                                    border="1px solid"
                                    sx={customScrollbar}
                                >
                                    <SearchSuggestions
                                        closeAndNavigate={closeAndNavigate}
                                        recentSearches={recentSearches}
                                        searchSuggestions={searchSuggestions}
                                        noResultObject={noResult}
                                        onSubmitSearch={onSubmitSearch}
                                    />
                                </PopoverContent>
                            )}
                        </HideOnMobile>

                        <HideOnDesktop position="relative" zIndex="9999">
                            <Box
                                overflowY="auto"
                                maxH={{base: '500px', sm: '23em'}}
                                position="absolute"
                                right={0}
                                w="100%"
                                sx={customScrollbar}
                            >
                                <Flex
                                    display={
                                        isOpen || searchInputRef?.value?.length > 0
                                            ? 'block'
                                            : 'none'
                                    }
                                    background="white"
                                >
                                    {(searchSuggestion.isSuccess || productSearch.isSuccess) && (
                                        <SearchSuggestions
                                            closeAndNavigate={closeAndNavigate}
                                            recentSearches={recentSearches}
                                            searchSuggestions={searchSuggestions}
                                            noResultObject={noResult}
                                            onSubmitSearch={onSubmitSearch}
                                        />
                                    )}
                                </Flex>
                            </Box>
                        </HideOnDesktop>
                    </form>
                </PopoverTrigger>
            </Box>
        </Popover>
    )
}

Search.displayName = 'SearchInput'

export default Search
